import { useStorage } from '@vueuse/core'
import { acceptHMRUpdate } from 'pinia'

import { getProductShopProductsProductIdGet } from '@/shared/api/intranet'

import { buyProducts } from '../api'
import { isFatalActualizeCartError } from '../lib/actualizeCartErrorConfig'
import { resolveActualizeCartError } from '../lib/resolveActualizeCartError'
import { resolveCreateOrderError } from '../lib/resolveCreateOrderError'
import {
  type ActualizeCartError,
  type AddToCartParams,
  type CartItem,
  type CreateOrderError,
  type CreateOrderParams,
  CREATE_ORDER_ERROR,
} from '../model'

export const useCart = defineStore('cart', () => {
  const items = useStorage<CartItem[]>('uplatform-store-cart', [])
  const getTotalPrice = computed(() => {
    return getInStockItems.value.reduce((total, product) => total + product.item.price * product.quantity, 0)
  })
  const getTotalQuantity = computed(() => items.value.reduce((total, product) => total + product.quantity, 0))
  const getOutOfStockItems = computed(() => {
    return items.value.filter(
      (item) => item.item.stock?.find((stock) => stock.id === item.product_sku_id)?.quantity === 0,
    )
  })
  const getInStockItems = computed(() => {
    return items.value.filter(
      (item) => item.item.stock?.find((stock) => stock.id === item.product_sku_id)?.quantity !== 0,
    )
  })
  const findProductInCart = (itemId: number, skuId: number): Nullable<CartItem> => {
    const item = items.value.find((product) => product.product_id === itemId && skuId === product.product_sku_id)
    return item || null
  }
  const actualizeCart = async (): Promise<ActualizeCartError | null> => {
    const tempItems: CartItem[] = []
    let itemError: ActualizeCartError | null = null

    for (const item of items.value) {
      const { data, error, response } = await getProductShopProductsProductIdGet({
        path: {
          product_id: item.product_id,
        },
      })

      if (error) {
        const resolved = resolveActualizeCartError(error, response?.status)

        if (isFatalActualizeCartError(resolved)) {
          return resolved
        }

        itemError ??= resolved
        continue
      }

      const stockQuantity = data?.stock.find((stock) => stock.id === item.product_sku_id)?.quantity
      if (stockQuantity && item.quantity > stockQuantity) {
        item.quantity = stockQuantity
      }
      tempItems.push({
        ...item,
        item: { ...data! },
      })
    }

    items.value = tempItems

    return itemError
  }
  const addProductToCart = async ({ item, onNotFound, onSuccess }: AddToCartParams) => {
    const {
      data: fullItem,
      error,
      response,
    } = await getProductShopProductsProductIdGet({
      path: {
        product_id: item.product_id,
      },
    })

    if (error && response?.status === 404) {
      await onNotFound?.()
      const product = findProductInCart(item.product_id, item.product_sku_id)
      if (product) {
        removeProductFromCart(product)
      }
      return
    }

    if (fullItem?.is_deleted) {
      await onNotFound?.()
      const product = findProductInCart(item.product_id, item.product_sku_id)
      if (product) {
        removeProductFromCart(product)
      }
      return
    }

    const product = findProductInCart(item.product_id, item.product_sku_id)
    if (product) {
      product.quantity += item.quantity
    } else {
      items.value.push({
        item: { ...fullItem! },
        ...item,
      })
    }
    await onSuccess?.()
  }
  const setProductQuantity = (product: CartItem, quantity: number) => {
    const item = findProductInCart(product.product_id, product.product_sku_id)
    if (item) {
      item.quantity = quantity
    }
  }
  const removeProductFromCart = (product: CartItem) => {
    const item = findProductInCart(product.product_id, product.product_sku_id)
    if (item) {
      items.value.splice(items.value.indexOf(item), 1)
    }
  }
  const clearCart = () => {
    items.value = []
  }
  const router = useRouter()
  const createOrder = async (fetchParams: CreateOrderParams): Promise<CreateOrderError | null> => {
    const { error, response } = await buyProducts(getInStockItems.value, fetchParams)

    if (error) {
      return resolveCreateOrderError(error, response?.status)
    }

    const hasOutOfStockItems = getOutOfStockItems.value.length > 0

    clearCart()
    router.push({ name: '/my-orders/[[page]]' })

    return hasOutOfStockItems ? CREATE_ORDER_ERROR.outOfStock : null
  }

  const $reset = () => {
    clearCart()
  }
  return {
    items,
    getTotalPrice,
    getTotalQuantity,
    getOutOfStockItems,
    getInStockItems,
    findProductInCart,
    actualizeCart,
    addProductToCart,
    setProductQuantity,
    removeProductFromCart,
    clearCart,
    createOrder,
    $reset,
  }
})
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCart, import.meta.hot))
}
